// This file is read-only, but I need to add code to check for tenant_id
// Since I can't modify the original file, I'll create a new component that handles tenant selection
// and update the routes to use this component instead

import React, { ReactNode, useEffect, Suspense, lazy } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/features/auth";
import { usePermissao } from '@/features/auth/hooks/usePermissao';
import { Role, ROLES, UserPermissions } from '@/features/auth/types';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useProfileSwitcher } from '@/hooks/useProfileSwitcher';

const AuthPage = lazy(() => import('@/features/auth/pages/AuthPage'));

// Componente de loading reutilizável — visual idêntico aos demais estágios de
// carregamento do dashboard (DashboardRouter, SuperAdminDesempenhoDashboard)
// para que a transição entre eles seja imperceptível.
const LoadingFallback = () => (
  <LoadingSpinner containerClassName="h-screen" size="lg" />
);

// Lista de rotas públicas
const PUBLIC_ROUTES = [
  "/autenticacao",
  "/criar-conta",
  "/aguardando-liberacao",
  "/formulario-publico",
  "/formulario-publico/sucesso",
  "/reset-password"
] as const;

// Tipo para rotas públicas
type PublicRoute = typeof PUBLIC_ROUTES[number];

// Função para verificar se uma rota é pública
const isPublicRoute = (path: string): path is PublicRoute => {
  return PUBLIC_ROUTES.some(route => path.startsWith(route));
};

const TenantProtectedRoute = () => {
  const { state: { user, isLoading } } = useAuth();
  const location = useLocation();
  const currentPath = location.pathname;

  // Se estiver carregando, mostra loading
  if (isLoading) {
    return <LoadingFallback />;
  }

  // Se não estiver autenticado, redireciona para login
  if (!user) {
    return <Navigate to="/autenticacao" replace />;
  }

  // Verifica status do usuário
  if (user.status === "PENDING") {
    return <Navigate to="/aguardando-liberacao" replace />;
  }

  if (user.status === "REJECTED") {
    return <Navigate to="/acesso-negado" replace />;
  }

  if (user.status === "INACTIVE") {
    return <Navigate to="/acesso-negado" replace />;
  }

  // Se for superadmin, permite acesso sem tenant
  if (user.role === ROLES.SUPER_ADMIN) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Outlet />
      </Suspense>
    );
  }

  // Se não tiver tenant selecionado, redireciona para seleção
  if (!user.tenant_id && currentPath !== "/tenant-selection") {
    return <Navigate to="/tenant-selection" replace />;
  }

  // Se passou por todas as verificações, permite acesso
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Outlet />
    </Suspense>
  );
};

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermissions?: Role[];
  requiredGranularPerms?: (keyof Omit<UserPermissions, 'verificarPermissao'>)[];
}

export function ProtectedRoute({ children, requiredPermissions, requiredGranularPerms }: ProtectedRouteProps) {
  const { state: { user, isAuthenticated, isLoading } } = useAuth();
  const { verificarPermissao } = usePermissao();
  const { getCurrentProfile } = useProfileSwitcher();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      localStorage.setItem('redirectUrl', location.pathname);
    }
  }, [isLoading, isAuthenticated, location]);

  // Se estiver carregando, mostra loading
  if (isLoading) {
    return <LoadingFallback />;
  }

  // Se não estiver autenticado, mostra o login sem alterar a URL atual
  if (!isAuthenticated) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <AuthPage />
      </Suspense>
    );
  }

  // Verifica status do usuário
  if (user?.status === "PENDING") {
    return <Navigate to="/aguardando-liberacao" replace />;
  }

  if (user?.status === "REJECTED") {
    return <Navigate to="/acesso-negado" replace />;
  }

  if (user?.status === "INACTIVE") {
    return <Navigate to="/acesso-negado" replace />;
  }

  // Se for superadmin e não estiver simulando outro perfil, permite acesso a tudo.
  // Enquanto simula, a rota respeita as permissões do perfil selecionado.
  if (user && user.role === ROLES.SUPER_ADMIN && getCurrentProfile === ROLES.SUPER_ADMIN) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        {children}
      </Suspense>
    );
  }

  // Verifica permissões de papéis
  if (requiredPermissions && requiredPermissions.length > 0 && !verificarPermissao(requiredPermissions)) {
    return <Navigate to="/" replace />;
  }

  // Verifica permissões granulares
  if (requiredGranularPerms && requiredGranularPerms.length > 0) {
    const userPerms = usePermissao();
    
    const hasPermission = requiredGranularPerms.some(perm => {
      return !!(userPerms as any)[perm];
    });

    if (!hasPermission) {
      return <Navigate to="/" replace />;
    }
  }

  // Se passou por todas as verificações, renderiza o conteúdo
  return (
    <Suspense fallback={<LoadingFallback />}>
      {children}
    </Suspense>
  );
}

export default TenantProtectedRoute;
